import { useState } from "react";
import { Box, Typography } from "@mui/material";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import BoltIcon from "@mui/icons-material/Bolt";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import ChartCard, { Segmented } from "./ChartCard";
import SortedBarChart from "./SortedBarChart";
import KpiTile from "./KpiTile";
import AnalyticsHero, { periodLabel } from "./AnalyticsHero";
import { COLORS, GAP, SERIES, fmtCount, fmtDuration, rise } from "./emsTheme";

const MEASURES = [
    { key: "avg", label: "Average per ticket", pick: (r) => r.avgMinutes },
    { key: "total", label: "Total", pick: (r) => r.totalMinutes },
];

const durationColumns = (first) => [
    { key: "label", label: first },
    { key: "ticketCount", label: "Tickets", align: "right", text: (r) => fmtCount(r.ticketCount) },
    { key: "avgMinutes", label: "Average", align: "right", text: (r) => fmtDuration(r.avgMinutes) },
    { key: "totalMinutes", label: "Total", align: "right", text: (r) => fmtDuration(r.totalMinutes) },
];

const toBars = (rows, measure) => (rows || [])
    .map((r) => ({ key: r.key, label: r.label, value: measure.pick(r) }))
    .filter((r) => r.value != null);

const DurationBars = ({ rows, label }) => (
    <SortedBarChart rows={rows} focusTop formatValue={fmtDuration} ariaLabel={label} />
);

const avgOf = (r) => r.avgMinutes;
const sharePct = (n, total) => (total ? Math.round((n * 100) / total) : 0);

/** Every status's share of all the time tickets spent open, on one bar, with a ranked list. */
const TimeShareCard = ({ rows, loading }) => {
    const [hover, setHover] = useState(null);
    const list = [...rows].filter((r) => r.totalMinutes > 0).sort((a, b) => b.totalMinutes - a.totalMinutes);
    const total = list.reduce((a, r) => a + r.totalMinutes, 0);
    const top = list[0];
    const colour = (i) => SERIES[i % SERIES.length];
    return (
        <ChartCard loading={loading} eyebrow="Where the time goes" empty={!loading && !list.length} emptyHint="No status history for these filters." height={200}
            title={top ? `${sharePct(top.totalMinutes, total)}% of all ticket time is spent in ${top.label}` : "Share of time by status"}
            info="Total time all tickets spent in each status, as a share of the time they spent in any status before being resolved or closed."
            fileName="ems-time-share-by-status"
            table={{ columns: [{ key: "label", label: "Status" }, { key: "share", label: "Share", align: "right", text: (r) => `${sharePct(r.totalMinutes, total)}%` },
                { key: "totalMinutes", label: "Total time", align: "right", text: (r) => fmtDuration(r.totalMinutes) },
                { key: "avg", label: "Average", align: "right", text: (r) => fmtDuration(avgOf(r)) }], rows: list }}>
            <Box sx={{ display: "grid", gap: 2 }}>
                <Box sx={{ display: "flex", height: 22, borderRadius: "11px", overflow: "hidden", gap: "2px", bgcolor: "#F3F1F7" }}>
                    {list.map((r, i) => (
                        <Box key={r.key} title={`${r.label}: ${sharePct(r.totalMinutes, total)}% of ticket time`} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                            sx={{ flexGrow: r.totalMinutes, flexBasis: 0, minWidth: 6, bgcolor: colour(i), opacity: hover == null || hover === i ? 1 : 0.4, transition: "opacity 150ms" }} />
                    ))}
                </Box>
                <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }, gap: 0.5, columnGap: 3 }}>
                    {list.map((r, i) => (
                        <Box component="li" key={r.key} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                            sx={{ display: "grid", gridTemplateColumns: "10px minmax(0, 1fr) auto 44px", gap: 1.25, alignItems: "center", px: 1, py: 0.75, borderRadius: "8px",
                                bgcolor: hover === i ? COLORS.ground : "transparent", transition: "background-color 150ms" }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: colour(i) }} />
                            <Typography title={r.label} sx={{ fontSize: 13, color: COLORS.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label}</Typography>
                            <Typography sx={{ fontSize: 12, color: COLORS.ink3, fontVariantNumeric: "tabular-nums" }}>average {fmtDuration(avgOf(r))}</Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.ink, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{sharePct(r.totalMinutes, total)}%</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </ChartCard>
    );
};

/**
 * Ticket lifecycle: how long tickets sit in each status (from status history) and how long each
 * assignee's tickets stay open. Average per ticket, calendar time; time after a ticket is
 * resolved or closed isn't counted.
 */
const LifecycleSection = ({ data, loading, filters, statusList }) => {
    const [measureKey, setMeasureKey] = useState("avg");
    const measure = MEASURES.find((m) => m.key === measureKey);
    const byStatus = data?.lifecycle?.byStatus || [];
    const byAgent = data?.lifecycle?.byAgent || [];
    const statusBars = toBars(byStatus, measure);
    const agentBars = toBars(byAgent, measure);
    const longest = statusBars.slice().sort((a, b) => b.value - a.value)[0];
    const slowestAgent = agentBars.slice().sort((a, b) => b.value - a.value)[0];
    const toggle = <Segmented label="Measure" value={measureKey} onChange={setMeasureKey} options={MEASURES.map((m) => ({ value: m.key, label: m.label }))} />;
    const first = !data && loading;
    const common = { loading: first, height: 240 };

    // Headline tiles always use the average time, whatever the chart toggle says, and only
    // the working statuses: time "in Resolved" is time before a reopen, not waiting for help.
    const done = new Set((statusList || []).filter((st) => st.category === "RESOLVED" || st.category === "CLOSED").map((st) => st.label));
    const working = byStatus.filter((r) => !done.has(r.label) && !done.has(r.key));
    const avgStatus = working.filter((r) => avgOf(r) != null).sort((a, b) => avgOf(b) - avgOf(a));
    const avgAgent = byAgent.filter((r) => avgOf(r) != null).sort((a, b) => avgOf(b) - avgOf(a));
    const slowStage = avgStatus[0], fastStage = avgStatus[avgStatus.length - 1], slowAgent = avgAgent[0];
    const measured = byStatus.reduce((a, r) => Math.max(a, r.ticketCount || 0), 0);
    const agentName = (r) => (r.key === "unassigned" ? "Unassigned tickets" : r.label);

    return (
        <Box sx={{ display: "grid", gap: 2.5, opacity: loading && data ? 0.7 : 1, transition: "opacity 0.15s" }}>
            <AnalyticsHero
                pill={`${periodLabel(filters)} · calendar time`}
                story={first ? "Working out the numbers…" : slowStage
                    ? `Tickets wait longest in ${slowStage.label}, ${fmtDuration(avgOf(slowStage))} on average.`
                    : "No status history for these filters yet."}
                footer={<Typography sx={{ fontSize: 13, opacity: 0.85 }}>From each ticket's status history. Headline tiles cover the working statuses (not Resolved or Closed); the charts show every status.</Typography>}
                tiles={(
                    <>
                        <KpiTile loading={first} label="Longest wait" value={slowStage ? fmtDuration(avgOf(slowStage)) : "No data"} accent="orange" icon={HourglassBottomIcon}
                            caption={slowStage ? `${slowStage.label}, average per ticket` : "Average time per ticket"} sx={rise(1)} />
                        <KpiTile loading={first} label="Quickest step" value={fastStage && fastStage !== slowStage ? fmtDuration(avgOf(fastStage)) : "No data"} accent="green" icon={BoltIcon}
                            caption={fastStage && fastStage !== slowStage ? `${fastStage.label}, average per ticket` : "Needs two or more working statuses"} sx={rise(2)} />
                        <KpiTile loading={first} label="Open longest" value={slowAgent ? fmtDuration(avgOf(slowAgent)) : "No data"} accent="red" icon={PersonSearchOutlinedIcon}
                            caption={slowAgent ? `${agentName(slowAgent)}, average time open` : "Average time open, by assignee"} sx={rise(3)} />
                        <KpiTile loading={first} label="Tickets measured" value={measured} format={fmtCount} accent="purple" icon={ConfirmationNumberOutlinedIcon}
                            caption={`Across ${fmtCount(byStatus.length)} ${byStatus.length === 1 ? "status" : "statuses"}`} sx={rise(4)} />
                    </>
                )}
            />

            <Box sx={rise(5)}><TimeShareCard rows={byStatus} loading={first} /></Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(2, minmax(0, 1fr))" }, gap: GAP, alignItems: "start", ...rise(6) }}>
                <ChartCard {...common} eyebrow="Time in each status" empty={!first && !statusBars.length} emptyHint="No status history for these filters."
                    title={longest ? `Tickets spend the longest in ${longest.label}, ${fmtDuration(longest.value)} (${measure.label})` : "Time spent in each status"}
                    info="From each ticket's status history: time between entering a status and leaving it. Average per ticket. Calendar time; time after resolution isn't counted."
                    actions={toggle} fileName="ems-time-per-status" table={{ columns: durationColumns("Status"), rows: byStatus }}>
                    <DurationBars rows={statusBars} label="Time per status" />
                </ChartCard>
                <ChartCard {...common} eyebrow="Time open by assignee" empty={!first && !agentBars.length}
                    title={slowestAgent ? `${slowestAgent.key === "unassigned" ? "Unassigned tickets stay" : `${slowestAgent.label}'s tickets stay`} open the longest, ${fmtDuration(slowestAgent.value)} (${measure.label})` : "Time open by assignee"}
                    info="Open and pending time of each assignee's tickets (current assignee). Calendar time."
                    actions={toggle} fileName="ems-time-open-by-agent" table={{ columns: durationColumns("Assignee"), rows: byAgent }}>
                    <DurationBars rows={agentBars} label="Time open by assignee" />
                </ChartCard>
            </Box>
        </Box>
    );
};

export default LifecycleSection;
